<template>
  <!-- Shown in a hub tab when its session dropped: the tab keeps its place
       instead of bouncing to the launcher. Reconnect re-establishes the same
       server, Close drops the tab. -->
  <Teleport to="body">
    <div class="fixed inset-0 z-[160] flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm">
      <div
        class="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        <span class="material-symbols-outlined text-4xl" :class="isOffline ? 'text-amber-500' : 'text-rose-500'">
          {{ isOffline ? 'wifi_off' : 'link_off' }}
        </span>
        <h3 class="mt-3 text-lg font-semibold text-gray-900 dark:text-white">
          {{ t('connectionLost.title') }}
        </h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {{ detail }}
        </p>
        <!-- Why the reconnect did not open the hub, in the vocabulary every other
             connect surface answers with (`hub.open.failure.*`); the store's own
             line is the last resort for a failure outside it. -->
        <p v-if="failureSentence" class="mt-2 text-xs font-medium text-rose-500" data-reconnect-failure>
          {{ failureSentence }}
        </p>
        <p v-else-if="store.error" class="mt-2 text-xs font-medium text-rose-500">
          {{ store.error }}
        </p>

        <div class="mt-5 flex items-center justify-center gap-2">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-full bg-primary-400 px-4 py-2 text-sm font-semibold text-gray-900 transition-colors hover:bg-primary-500 disabled:opacity-60"
            :disabled="store.reconnecting"
            @click="store.reconnect()">
            <span class="material-symbols-outlined text-lg" :class="{ 'animate-spin': store.reconnecting }">{{
              store.reconnecting ? 'progress_activity' : 'refresh'
            }}</span>
            {{ store.reconnecting ? t('connectionLost.reconnecting') : t('connectionLost.reconnect') }}
          </button>
          <button
            type="button"
            class="rounded-full border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            @click="close">
            {{ t('connectionLost.close') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '../stores/auth';
import { useConnectionLostStore } from '../stores/connectionLost';
import { hubOpenMessageKey } from '../utils/hubOpenFallback';

const { t } = useI18n();
const store = useConnectionLostStore();
const authStore = useAuthStore();

const isOffline = computed(() => store.reason === 'offline');

const hub = computed(() => (store.hubName ? store.hubName : t('connectionLost.thisServer')));

const detail = computed(() => {
  return t(isOffline.value ? 'connectionLost.offline' : 'connectionLost.session', { hub: hub.value });
});

/**
 * Why the reconnect did not open the hub. The store keeps the refusal and the
 * hub's own code; the sentence is chosen here, because translation lives in the
 * components — the same precedence `components/server/ServersSection.vue` uses
 * for a connect that refused.
 */
const failureSentence = computed(() => {
  const key = store.messageKey ?? (store.failure ? hubOpenMessageKey(store.failure) : null);
  if (!key) return null;
  return t(key, {
    hub: hub.value,
    address: store.address ?? '—',
    email: authStore.cloudUser?.email || '',
  });
});

/** Close the tab this overlay lives in; outside the shell just hide it. */
async function close(): Promise<void> {
  const api = window.electron;
  const tabInfo = await Promise.resolve(api?.getTabInfo?.()).catch(() => null);
  if (api?.closeSelfTab && tabInfo?.tabbed) {
    await api.closeSelfTab();
    return;
  }
  store.clear();
}
</script>
